/**
 * The application page: a tapped home opens a confirmation sheet, and the
 * sheet's button opens the registration form with that home filled in.
 *
 * Every row is already a working link to its pre-filled form, so without this
 * script the page still applies; this only puts one calm step in between that
 * names the home and says what the form will ask. The sheet behaves as the
 * phone menu's does (pastel.js): the scrim, the close button, Escape, a tap on
 * the handle and a pull down all close it, and focus stays inside while open.
 *
 * A home of ten or more is two or three flats, and its group applies flat by
 * flat (owner, 15 October 2026). Its row carries no form link of its own: the
 * sheet lists the flats instead, each with its own pre-filled Start link, and
 * stays open after one is used so the next group's link is still there. The
 * 14-bed's 6-bed flat can be either, so that card carries a Start per open
 * flat; flats switched off in the admin never reach the page.
 *
 * ?home=<slug> opens the sheet for that home on arrival, so a property page
 * can link straight here. A slug with two flats on the form (North Road and
 * Queen Street) highlights both rows instead of guessing which one.
 */
(function () {
	'use strict';

	var root = document.querySelector('[data-pastel-apply]');
	var sheet = document.querySelector('[data-apply-sheet]');
	if (!root || !sheet) { return; }

	var panel = sheet.querySelector('[data-apply-panel]');
	var photo = sheet.querySelector('[data-apply-sheet-photo]');
	var nameEl = sheet.querySelector('[data-apply-sheet-name]');
	var lineEl = sheet.querySelector('[data-apply-sheet-line]');
	var start = sheet.querySelector('[data-apply-start]');
	var note = sheet.querySelector('[data-apply-sheet-note]');
	var flatsEl = sheet.querySelector('[data-apply-sheet-flats]');
	var stepsEl = sheet.querySelector('[data-apply-sheet-steps]');
	var noteText = note ? note.textContent : '';
	var view = sheet.querySelector('[data-apply-view]');
	var handle = sheet.querySelector('.pastel-apply-handle');
	var fallbackPhoto = photo ? photo.innerHTML : '';
	var rows = Array.prototype.slice.call(root.querySelectorAll('[data-apply-home]'));
	var current = null;
	var closeTimer = 0;
	var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	function baytTrack(name, params) {
		if (typeof window.baytTrack === 'function') { window.baytTrack(name, params || {}); }
	}

	function fill(row) {
		var src = row.getAttribute('data-apply-photo') || '';
		var tone = row.getAttribute('data-apply-tone') || '';

		photo.className = 'pastel-apply-sheet-photo' + (src ? '' : ' pastel-apply-tone-' + tone);
		photo.innerHTML = fallbackPhoto;
		if (src) {
			var img = document.createElement('img');
			img.src = src;
			img.alt = '';
			img.decoding = 'async';
			photo.innerHTML = '';
			photo.appendChild(img);
		}

		nameEl.textContent = row.getAttribute('data-apply-name') || '';
		lineEl.textContent = row.getAttribute('data-apply-line') || '';

		// A big home lists its flats, each with its own form, in place of Start.
		var split = splitOf(row);
		flatsEl.innerHTML = '';
		if (split) {
			Array.prototype.forEach.call(split.querySelectorAll('.pastel-apply-flat'), function (flat) {
				flatsEl.appendChild(flat.cloneNode(true));
			});
		}
		flatsEl.hidden = !split;
		sheet.classList.toggle('is-split', !!split);
		stepsEl.hidden = !!split;
		start.hidden = !!split;
		note.textContent = split ? (row.getAttribute('data-apply-note') || noteText) : noteText;
		if (!split) { start.href = row.href; }

		var homeUrl = row.getAttribute('data-apply-home-url') || '';
		view.hidden = !homeUrl;
		view.href = homeUrl || '#';
	}

	function splitOf(row) {
		var id = row.getAttribute('data-apply-split');
		return id ? document.getElementById(id) : null;
	}

	function focusables() {
		return Array.prototype.filter.call(
			panel.querySelectorAll('a[href], button:not([tabindex="-1"])'),
			function (el) { return !el.hidden && el.offsetParent !== null; }
		);
	}

	function open(row, how) {
		window.clearTimeout(closeTimer);
		rows.forEach(function (r) { r.classList.toggle('is-chosen', r === row); });
		current = row;
		fill(row);

		sheet.hidden = false;
		document.documentElement.classList.add('pastel-apply-locked');
		// One frame hidden-to-shown before the class, so the rise animates.
		window.requestAnimationFrame(function () {
			window.requestAnimationFrame(function () { sheet.classList.add('is-open'); });
		});
		/*
		 * A keyboard user lands on the button; a tap focuses the dialog
		 * itself, so pastel.css's focus ring does not frame the button as if
		 * it had been tabbed to.
		 */
		var first = start.hidden ? flatsEl.querySelector('a') : start;
		('key' === how && first ? first : panel).focus({ preventScroll: true });

		baytTrack('apply_home_select', {
			home: row.getAttribute('data-apply-home') || '',
			option: row.getAttribute('data-apply-option') || '',
			how: how || 'tap'
		});
	}

	function close(method) {
		if (sheet.hidden) { return; }
		sheet.classList.remove('is-open');
		sheet.classList.remove('is-dragging');
		panel.style.transform = '';
		document.documentElement.classList.remove('pastel-apply-locked');
		closeTimer = window.setTimeout(function () { sheet.hidden = true; }, reduce ? 200 : 320);

		if (current) {
			current.classList.remove('is-chosen');
			current.focus({ preventScroll: true });
			baytTrack('apply_sheet_close', { home: current.getAttribute('data-apply-home') || '', method: method || 'button' });
		}
	}

	rows.forEach(function (row) {
		// With this script a tap opens the sheet, not the form, so say so.
		row.setAttribute('aria-haspopup', 'dialog');
		row.setAttribute('aria-label', 'Apply for ' + (row.getAttribute('data-apply-name') || ''));
		row.addEventListener('click', function (event) {
			// A new-tab click or a middle click keeps the link's own behaviour.
			if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) { return; }
			event.preventDefault();
			// A click with no pointer behind it (detail 0) came from Enter.
			open(row, 0 === event.detail ? 'key' : 'tap');
		});
	});

	start.addEventListener('click', function () {
		if (!current) { return; }
		baytTrack('apply_start', {
			home: current.getAttribute('data-apply-home') || '',
			option: current.getAttribute('data-apply-option') || ''
		});
		// The form opens in a new tab; this tab is left as it was found.
		window.setTimeout(function () { close('start'); }, 250);
	});

	// A flat's Start leaves the sheet open: the other flats' groups still need theirs.
	flatsEl.addEventListener('click', function (event) {
		// A whole flat card, or one of the 14-bed's two flat pills.
		var flat = event.target.closest && event.target.closest('[data-apply-flat-option]');
		if (!flat || !current) { return; }
		baytTrack('apply_start', {
			home: current.getAttribute('data-apply-home') || '',
			option: flat.getAttribute('data-apply-flat-option') || '',
			split: true
		});
	});

	view.addEventListener('click', function () {
		if (current) { baytTrack('apply_view_home', { home: current.getAttribute('data-apply-home') || '' }); }
	});

	Array.prototype.forEach.call(sheet.querySelectorAll('[data-apply-close]'), function (el) {
		el.addEventListener('click', function () {
			close(el === handle ? 'handle' : (el.classList.contains('pastel-apply-scrim') ? 'scrim' : 'button'));
		});
	});

	document.addEventListener('keydown', function (event) {
		if (sheet.hidden) { return; }
		if ('Escape' === event.key) {
			event.preventDefault();
			close('escape');
			return;
		}
		if ('Tab' !== event.key) { return; }
		var items = focusables();
		if (!items.length) { return; }
		var first = items[0];
		var last = items[items.length - 1];
		if (event.shiftKey && document.activeElement === first) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && document.activeElement === last) {
			event.preventDefault();
			first.focus();
		}
	});

	/*
	 * Pull down to close, from the handle, or from anywhere while the body is
	 * scrolled to its top, as the property page's sheets close.
	 */
	var body = sheet.querySelector('.pastel-apply-sheet-body');
	var dragStart = null;
	var dragDelta = 0;

	panel.addEventListener('touchstart', function (event) {
		var onHandle = handle && handle.contains(event.target);
		if (!onHandle && body && body.scrollTop > 0) { dragStart = null; return; }
		dragStart = event.touches[0].clientY;
		dragDelta = 0;
	}, { passive: true });

	panel.addEventListener('touchmove', function (event) {
		if (null === dragStart) { return; }
		dragDelta = Math.max(0, event.touches[0].clientY - dragStart);
		if (dragDelta < 6) { return; }
		event.preventDefault();
		sheet.classList.add('is-dragging');
		panel.style.transform = 'translateY(' + dragDelta + 'px)';
	}, { passive: false });

	panel.addEventListener('touchend', function () {
		if (null === dragStart) { return; }
		dragStart = null;
		sheet.classList.remove('is-dragging');
		if (dragDelta > Math.min(140, panel.offsetHeight * 0.25)) {
			close('drag');
		} else {
			panel.style.transform = '';
		}
	});

	root.addEventListener('click', function (event) {
		var tile = event.target.closest && event.target.closest('[data-apply-tile]');
		if (tile) { baytTrack('apply_tile', { band: tile.getAttribute('data-apply-tile') || '' }); }
	});

	// ?home=<slug>: open that home, or mark its flats when the form lists two.
	var wanted = '';
	try { wanted = new URLSearchParams(window.location.search).get('home') || ''; } catch (e) { wanted = ''; }
	if (wanted) {
		var matches = rows.filter(function (row) { return row.getAttribute('data-apply-home') === wanted; });
		if (1 === matches.length) {
			open(matches[0], 'link');
		} else if (matches.length > 1) {
			matches.forEach(function (row) { row.classList.add('is-chosen'); });
			matches[0].scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
		}
	}
})();
